import * as React from 'react';
import classNames from 'classnames';

import { domToReact, htmlToDOM } from 'html-react-parser';
import { Popover, PopoverInteractionKind, } from '@blueprintjs/core';
import { createPortal } from 'react-dom';

import Menu from '../Menu';
import MenuItem from '../MenuItem';
import Overlay from '../Overlay';
import Icon from '../Icon';
import Collapsible from '../Collapsible';
import CollapsibleBody from '../CollapsibleBody';
import CollapsibleHeader from '../CollapsibleHeader';
import { useTimeout } from '../../Utilities/Generic';

export interface ISiteMenuProps {
    /* DOM to parse */
    dom: string;
    /** Open mobile menu event */
    openMobileMenu: (e) => void;
    /** Close mobile menu event */
    closeMobileMenu: (e) => void;
    /** Should mobile menu be open */
    mobileMenuOpen: boolean;
    /** Popover target to render popovers */
    target?: string;
    /** Theme to use in classes */
    theme?: string;
    /** Backdrop click closes overlay */
    canOutsideClickClose?: boolean;
    /** Layout to use */
    layout?: 'default' | 'inline';
}

const Toggler: React.FunctionComponent<{
    href?: string;
    defaultOpen?: boolean;
    className?: string;
}> = (props) => {
    const [open, setIsOpen] = React.useState(props.defaultOpen || false);
    return (
        <React.Fragment>
            <a
                href={props.href || '#'}
                className={classNames(props.className, open ? 'open' : '')}
                onClick={(e) => {
                    e.preventDefault();
                    setIsOpen(!open);
                }}
            >
                <Icon
                    width={16}
                    height={16}
                    kind={open ? 'arrow5-up' : 'arrow5-down'}
                />
                {props.children}
            </a>
        </React.Fragment>
    );
};

const SiteMenu: React.FunctionComponent<ISiteMenuProps> = (props) => {
    const target = props.target && document.getElementById(props.target) || null;
    const theme = props.theme ? props.theme : 'ideal';
    const [dropContent, setDropContent] = React.useState<React.ReactNode>(null);
    const [dropOpen, setDropOpen] = React.useState<React.ReactNode>(null);
    const [timerOpen, setTimerOpen] = React.useState<number | null>(null);
    const [timerClose, setTimerClose] = React.useState<number | null>(null);

    useTimeout(() => {
        setTimerOpen(null);
        setDropOpen(true);
    }, timerOpen);

    useTimeout(() => {
        setTimerClose(null);
        setDropOpen(false);
    }, timerClose);

    const parserOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.name === 'nav') {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <nav
                            {...rest}
                            className={className}
                        >
                            {domToReact(domNode.children, parserOptions)}
                        </nav>
                    );
                }

                if (domNode.name === 'a' && !!domNode.next) {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, ...rest } = domNode.attribs;
                    const isMenuToggler = className?.split(' ').includes('menutoggler') || false;
                    const isActive = className?.split(' ').includes('active') || false;
                    const nextIsGrouped = domNode.next.attribs.class?.split(' ').includes('grouped') || false;

                    if (isMenuToggler) {
                        return (
                            <a
                                {...rest}
                                className={className}
                                onClick={props.openMobileMenu}
                            >{inner}</a>
                        );
                    }

                    if (props.layout === 'inline' && domNode.next.name === 'ul' && domNode.attribs.href !== '#') {
                        return (
                            <Collapsible defaultOpen={isActive} layout="menu-item">
                                <CollapsibleHeader
                                    title={(
                                        <a
                                            {...rest}
                                            className={className}
                                            suppressHydrationWarning={true}>
                                            {inner}
                                        </a>
                                    )}
                                />
                                <CollapsibleBody>
                                    {nextIsGrouped ? (
                                        <ul className="grouped">
                                            {domToReact(domNode.next.children, parserSubOptions)}
                                        </ul>
                                    ) : (
                                        <Menu>
                                            {domToReact(domNode.next.children, parserSubOptions)}
                                        </Menu>
                                    )}
                                </CollapsibleBody>
                            </Collapsible>
                        );
                    }

                    if (props.layout === 'inline' && domNode.next.name === 'ul') {
                        return (
                            <Toggler
                                {...rest}
                                defaultOpen={isActive}
                                className={className}
                            >
                                {inner}
                            </Toggler>
                        );
                    }

                    return (
                        <a
                            {...rest}
                            className={classNames('bp3-menu-item', className)}
                            suppressHydrationWarning={true}
                        >
                            {inner}
                            {target && nextIsGrouped ? (
                                <span
                                    className="dropper"
                                    onMouseEnter={() => {
                                        setTimerClose(null);
                                        setDropContent(
                                            <ul className="grouped">
                                                {domToReact(domNode.next.children, {})}
                                            </ul>
                                        );
                                        setTimerOpen(150);
                                    }}
                                    onMouseLeave={() => {
                                        setTimerOpen(null);
                                        setTimerClose(500);
                                    }}
                                ><span/></span>
                            ) : (
                                <Popover
                                    enforceFocus={false}
                                    interactionKind={PopoverInteractionKind.HOVER}
                                    position="bottom-left"
                                    usePortal={true}
                                    minimal={true}
                                    autoFocus={false}
                                    modifiers={{
                                        offset: {
                                            offset: '0, 0',
                                        },
                                        flip: {
                                            enabled: false,
                                        },
                                    }}
                                    className="dropper"
                                    portalClassName={'site-menu-portal'}
                                    popoverClassName={'site-menu-popover'}
                                    content={(
                                        <Menu>
                                            {domToReact(domNode.next.children, parserSubOptions)}
                                        </Menu>
                                    )}
                                    target={(
                                        <span onMouseEnter={() => {
                                            setTimerClose(null);
                                            setDropOpen(false);
                                        }}><span/></span>
                                    )}
                                    transitionDuration={0}
                                    hoverOpenDelay={0}
                                    hoverCloseDelay={100}
                                />
                            )}
                        </a>
                    );
                }
            }

            if (props.layout === 'inline' && domNode.name === 'ul' && !!domNode.prev && domNode.prev.name === 'a' && domNode.prev.attribs.href !== '#') {
                return (
                    <React.Fragment/>
                );
            }

            return false;
        },
    };

    const parserSubOptions = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.name === 'li') {
                    return (
                        <React.Fragment>
                            {domToReact(domNode.children, parserSubOptions)}
                        </React.Fragment>
                    );
                }

                if (domNode.name === 'a' && !!domNode.next) {
                    const inner = domToReact(domNode.children, parserOptions);
                    const { class: className, ...rest } = domNode.attribs;
                    const isActive = className?.split(' ').includes('active') || false;
                    const nextIsGrouped = domNode.next.attribs.class?.split(' ').includes('grouped') || false;

                    if (props.layout === 'inline' && domNode.next.name === 'ul' && domNode.attribs.href !== '#') {
                        return (
                            <Collapsible defaultOpen={isActive} layout="menu-item">
                                <CollapsibleHeader
                                    title={(
                                        <a
                                            {...rest}
                                            className={className}
                                            suppressHydrationWarning={true}>
                                            {inner}
                                        </a>
                                    )}
                                />
                                <CollapsibleBody>
                                    {nextIsGrouped ? (
                                        <ul className="grouped">
                                            {domToReact(domNode.next.children, parserSubOptions)}
                                        </ul>
                                    ) : (
                                        <Menu>
                                            {domToReact(domNode.next.children, parserSubOptions)}
                                        </Menu>
                                    )}
                                </CollapsibleBody>
                            </Collapsible>
                        );
                    }

                    return (
                        <MenuItem
                            {...rest}
                            className={className}
                            text={domToReact(domNode.children, parserOptions)}
                        >
                            {/* domToReact(domNode.next.children, parserSubOptions) */}
                        </MenuItem>
                    );
                }

                if (domNode.name === 'a' && !domNode.next) {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <MenuItem
                            {...rest}
                            className={className}
                            text={domToReact(domNode.children, parserOptions)}
                        />
                    );
                }

                if (domNode.name === 'ul') {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <React.Fragment/>
                    );
                }
            }
            return false;
        },
    };

    const parserOptionsMobile = {
        replace: (domNode: any) => {
            if (domNode.name) {
                if (domNode.name === 'nav') {
                    const { class: className, ...rest } = domNode.attribs;
                    return (
                        <nav
                            {...rest}
                            className={className}
                        >
                            {domToReact(domNode.children, parserOptionsMobile)}
                        </nav>
                    );
                }

                if (domNode.name === 'a' && !!domNode.next) {
                    const inner = domToReact(domNode.children, parserOptionsMobile);
                    const { class: className, ...rest } = domNode.attribs;
                    const isMenuToggler = className?.split(' ').includes('menutoggler') || false;
                    const nextIsGrouped = domNode.next.attribs.class?.split(' ').includes('grouped') || false;

                    if (isMenuToggler) {
                        return (
                            <React.Fragment/>
                        );
                    }

                    return (
                        <Collapsible defaultOpen={false} layout={domNode.attribs.href === '#' && domNode.attribs['data-layout']!=='menu-item' ? 'default' : 'menu-item'}>
                            <CollapsibleHeader
                                title={domNode.attribs.href === '#' && domNode.attribs['data-layout']!=='menu-item' ? inner : (
                                    <a
                                        {...rest}
                                        className={className}
                                        suppressHydrationWarning={true}>
                                        {inner}
                                    </a>
                                )}
                            />
                            <CollapsibleBody>
                                {nextIsGrouped ? (
                                    <ul className="grouped">
                                        {domToReact(domNode.next.children, {})}
                                    </ul>
                                ) : (
                                    <Menu>
                                        {domToReact(domNode.next.children, parserOptionsMobile)}
                                    </Menu>
                                )}
                            </CollapsibleBody>
                        </Collapsible>
                    );
                }

                if (domNode.name === 'ul' && domNode.parent && domNode.parent.name !== 'nav') {
                    return (
                        <React.Fragment/>
                    );
                }
            }
            return false;
        },
    };

    // Main output

    const domTree = htmlToDOM(props.dom.replace(/>\s+</g, '><'));

    return (
        <React.Fragment>
            {domToReact(domTree, parserOptions)}
            {target ? createPortal((
                <div
                    className={classNames('site-menu__drop', { open: dropOpen, })}
                    onMouseEnter={() => {
                        setTimerClose(null);
                    }}
                    onMouseLeave={() => {
                        setTimerClose(500);
                    }}
                >
                    <div className={'site-menu__drop__limiter'}>
                        {dropContent}
                    </div>
                </div>
            ), target) : null}
            <Overlay
                isOpen={props.mobileMenuOpen}
                doClose={props.closeMobileMenu}
                layout="mainmenu"
                className="site-menu-mobile"
                customHeader={(
                    <div className="site-menu-mobile-heading">
                        <button onClick={props.closeMobileMenu}>
                            <Icon kind="close" width={16} height={16}/>
                        </button>
                    </div>
                )}
                canOutsideClickClose={props.canOutsideClickClose}
            >
                <div className="site-menu-mobile-content">
                    {domToReact(domTree, parserOptionsMobile)}
                </div>
            </Overlay>
        </React.Fragment>
    );
};

SiteMenu.displayName = 'SiteMenu';

export default SiteMenu;
